import {
  countWords,
  isCapitalizedMidSentence,
  phraseRegex,
  ratePer,
  sentenceIndexAt,
  splitSentences,
  type Tally,
  tally,
  withoutOverlaps,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Hedges and boosters — Ken Hyland's terms for the words that set how sure a writer
 * sounds. A hedge withholds full commitment ("the results suggest", "may", "possibly");
 * a booster asserts it ("clearly", "proves", "always"). Academic writing needs both, in
 * proportion to the evidence, so this module counts and places them and judges nothing.
 *
 * The lists are the core of Hyland's inventories, cut back where a word is ambiguous
 * enough to inflate the count: "would" and "should" (conditionals and obligations far
 * more often than hedges), "about" and "around" (prepositions), "show" and "find" (the
 * neutral way to report a result), and the filler words the Filler Word Finder owns.
 */

export type StanceCategory = "hedge" | "booster";

export type StanceKind =
  | "modal"
  | "verb"
  | "probability"
  | "frequency"
  | "approximator"
  | "certainty"
  | "absolute"
  | "proof"
  | "necessity";

export interface StanceMatch {
  start: number;
  end: number;
  text: string;
  category: StanceCategory;
  kind: StanceKind;
  /** Lowercase, single-spaced: the row a match is tallied under. */
  key: string;
}

export interface StackedSentence {
  start: number;
  end: number;
  text: string;
  count: number;
}

export interface StanceReport {
  words: number;
  matches: StanceMatch[];
  hedges: number;
  boosters: number;
  hedgesPer1000: number;
  boostersPer1000: number;
  /** Hedges per booster, or null when there are no boosters. */
  ratio: number | null;
  topHedges: Tally[];
  topBoosters: Tally[];
  /** Sentences with three or more hedges. */
  stackedHedges: StackedSentence[];
  /** Sentences with two or more boosters. */
  stackedBoosters: StackedSentence[];
}

interface StanceList {
  category: StanceCategory;
  kind: StanceKind;
  phrases: readonly string[];
}

const LISTS: readonly StanceList[] = [
  {
    category: "hedge",
    kind: "modal",
    phrases: ["could", "may", "might"],
  },
  {
    category: "hedge",
    kind: "verb",
    phrases: [
      "appear to",
      "appeared to",
      "appears to",
      "assume",
      "assumed",
      "assumes",
      "assuming",
      "indicate",
      "indicated",
      "indicates",
      "indicating",
      "it appears",
      "it would appear",
      "postulate",
      "postulated",
      "postulates",
      "seem",
      "seemed",
      "seeming",
      "seems",
      "speculate",
      "speculated",
      "speculates",
      "suggest",
      "suggested",
      "suggesting",
      "suggests",
      "tend to",
      "tended to",
      "tends to",
    ],
  },
  {
    category: "hedge",
    kind: "probability",
    phrases: [
      "a possible",
      "another possible",
      "apparently",
      "arguably",
      "conceivably",
      "it is possible",
      "it may be possible",
      "it might be possible",
      "it seems possible",
      "it was possible",
      "likely",
      "one possible",
      "perhaps",
      "plausible",
      "plausibly",
      "possible that",
      "possibly",
      "potentially",
      "presumably",
      "probable",
      "probably",
      "seemingly",
      "the possibility that",
      "uncertain",
      "unclear",
      "unlikely",
    ],
  },
  {
    category: "hedge",
    kind: "frequency",
    phrases: [
      "for the most part",
      "frequently",
      "generally",
      "in general",
      "in many cases",
      "in most cases",
      "largely",
      "mainly",
      "mostly",
      "often",
      "on the whole",
      "sometimes",
      "typically",
      "usually",
    ],
  },
  {
    category: "hedge",
    kind: "approximator",
    phrases: [
      "almost",
      "approximately",
      "in part",
      "more or less",
      "partially",
      "partly",
      "relatively",
      "roughly",
      "to a certain extent",
      "to a large extent",
      "to some degree",
      "to some extent",
    ],
  },
  {
    category: "booster",
    kind: "certainty",
    phrases: [
      "beyond a reasonable doubt",
      "beyond any doubt",
      "beyond doubt",
      "certain that",
      "certainly",
      "clear evidence",
      "clearly",
      "conclusive",
      "conclusively",
      "definitely",
      "definitive",
      "definitively",
      "doubtless",
      "evidently",
      "in fact",
      "incontrovertible",
      "incontrovertibly",
      "indeed",
      "indisputable",
      "indisputably",
      "irrefutable",
      "irrefutably",
      "it is certain",
      "it is clear",
      "it is evident",
      "it is obvious",
      "it was clear",
      "it was obvious",
      "no doubt",
      "obviously",
      "surely",
      "there is no question that",
      "undeniable",
      "undeniably",
      "undoubtedly",
      "unquestionably",
      "without a doubt",
      "without doubt",
    ],
  },
  {
    category: "booster",
    kind: "absolute",
    phrases: ["always", "never"],
  },
  {
    category: "booster",
    kind: "proof",
    phrases: [
      "demonstrate",
      "demonstrated",
      "demonstrates",
      "demonstrating",
      "established that",
      "establishes that",
      "prove",
      "proved",
      "proven",
      "proves",
      "proving",
    ],
  },
  {
    category: "booster",
    kind: "necessity",
    phrases: ["must"],
  },
];

const PATTERNS = LISTS.map((list) => ({
  list,
  regex: phraseRegex(list.phrases),
}));

const RE_SPACES = /\s+/g;
const RE_CURLY = /’/g;
const RE_NEXT_WORD = /^\s+(\p{L}+)/u;
const RE_PREVIOUS_WORD = /(\p{L}+)\s+$/u;

const STACKED_HEDGES_MIN = 3;
const STACKED_BOOSTERS_MIN = 2;

function keyOf(text: string): string {
  return text.toLowerCase().replace(RE_CURLY, "'").replace(RE_SPACES, " ");
}

function nextWord(text: string, end: number): string {
  return RE_NEXT_WORD.exec(text.slice(end, end + 24))?.[1]?.toLowerCase() ?? "";
}

function previousWord(text: string, start: number): string {
  return (
    RE_PREVIOUS_WORD.exec(
      text.slice(Math.max(0, start - 24), start)
    )?.[1]?.toLowerCase() ?? ""
  );
}

/**
 * Uses that are not stance at all: "May 2020" (a month), "could not" (inability more
 * often than doubt), and "as indicated in Table 2" (a pointer, not a hedge).
 */
function isFalseFriend(text: string, match: StanceMatch): boolean {
  if (match.key === "may") {
    return isCapitalizedMidSentence(text, match.start);
  }
  if (match.key === "could") {
    return nextWord(text, match.end) === "not";
  }
  if (match.key === "indicated" || match.key === "suggested") {
    return previousWord(text, match.start) === "as";
  }
  return false;
}

function collect(text: string): StanceMatch[] {
  const found: StanceMatch[] = [];
  for (const { list, regex } of PATTERNS) {
    for (const hit of text.matchAll(regex)) {
      const match: StanceMatch = {
        start: hit.index,
        end: hit.index + hit[0].length,
        text: hit[0],
        category: list.category,
        kind: list.kind,
        key: keyOf(hit[0]),
      };
      if (!isFalseFriend(text, match)) {
        found.push(match);
      }
    }
  }
  return withoutOverlaps(found);
}

function stacked(
  sentences: ReturnType<typeof splitSentences>,
  matches: readonly StanceMatch[],
  minimum: number
): StackedSentence[] {
  const counts = new Map<number, number>();
  for (const match of matches) {
    const index = sentenceIndexAt(sentences, match.start);
    if (index >= 0) {
      counts.set(index, (counts.get(index) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, count]) => count >= minimum)
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .map(([index, count]) => {
      const sentence = sentences[index];
      return {
        start: sentence.start,
        end: sentence.end,
        text: sentence.text,
        count,
      };
    });
}

export function findHedgesAndBoosters(text: string): StanceReport {
  const matches = collect(text);
  const hedgeMatches = matches.filter((m) => m.category === "hedge");
  const boosterMatches = matches.filter((m) => m.category === "booster");
  const words = countWords(text);
  const sentences = splitSentences(text);
  return {
    words,
    matches,
    hedges: hedgeMatches.length,
    boosters: boosterMatches.length,
    hedgesPer1000: ratePer(hedgeMatches.length, words, 1000),
    boostersPer1000: ratePer(boosterMatches.length, words, 1000),
    ratio:
      boosterMatches.length > 0
        ? hedgeMatches.length / boosterMatches.length
        : null,
    topHedges: tally(hedgeMatches.map((m) => m.key)),
    topBoosters: tally(boosterMatches.map((m) => m.key)),
    stackedHedges: stacked(sentences, hedgeMatches, STACKED_HEDGES_MIN),
    stackedBoosters: stacked(sentences, boosterMatches, STACKED_BOOSTERS_MIN),
  };
}
